import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import useGeneralStore from "store/general";
import { superAdminNavigation } from "utils/navigation";
import logo from "assets/img/logo.png";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SuperAdminSidebar({ sidebarOpen, setSidebarOpen }) {
  const { sidebarCollapsed } = useGeneralStore();
  const location = useLocation();

  const isCurrentItem = (item) => {
    return location.pathname === item.href;
  };

  const renderNavigationItem = (item, isMobile = false) => {
    const itemCurrent = isCurrentItem(item);

    return (
      <a
        href={item.href}
        className={classNames(
          itemCurrent
            ? "bg-purple-800 text-white"
            : "text-purple-200 hover:text-white hover:bg-purple-800",
          "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
          !isMobile && sidebarCollapsed ? "justify-center" : ""
        )}
        title={!isMobile && sidebarCollapsed ? item.name : ""}
      >
        <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
        {(isMobile || !sidebarCollapsed) && item.name}
      </a>
    );
  };

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-purple-900 px-6 pb-4 ring-1 ring-white/10">
                  <div className="flex h-16 shrink-0 items-center">
                    <img
                      className="h-8 w-auto"
                      src={logo}
                      alt="Super Admin Logo"
                    />
                    <span className="ml-3 text-sm font-semibold text-white">
                      Super Admin
                    </span>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul className="-mx-2 space-y-1">
                          {superAdminNavigation.map((item) => (
                            <li key={item.name}>
                              {renderNavigationItem(item, true)}
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarCollapsed ? "lg:w-16" : "lg:w-72"
        }`}
      >
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-purple-900 px-3 pb-4">
          <div className="flex h-16 shrink-0 items-center justify-center">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-x-3">
                <img
                  className="h-8 w-auto"
                  src={logo}
                  alt="Super Admin Logo"
                />
                <span className="text-sm font-semibold text-white">
                  Super Admin
                </span>
              </div>
            )}
            {sidebarCollapsed && (
              <img
                className="h-8 w-8 object-contain"
                src={logo}
                alt="Super Admin Logo"
              />
            )}
          </div>

          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {superAdminNavigation.map((item) => (
                    <li key={item.name}>{renderNavigationItem(item, false)}</li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}

